import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';

type BannerVersion = { label: string; final: boolean };

/** Says the version shown is not the document's latest, and opens the latest. */
export const DocumentVersionBanner = (props: {
  shown: BannerVersion;
  latest: BannerVersion;
  /** A route to the latest version, or a handler that moves to it in place. */
  openLatest: string | (() => void);
  className?: string;
}) => {
  const { shown, latest } = props;
  const parts = shown.final
    ? [`v${shown.label} is Final`, `v${latest.label} is newer${latest.final ? ' (Final)' : ''}`]
    : [
        `v${shown.label} is out of date`,
        `Latest is v${latest.label}${latest.final ? ' (Final)' : ''}`
      ];
  const actionLabel = `Open v${latest.label}`;
  const actionClass = 'font-semibold underline underline-offset-2 hover:no-underline';
  return (
    <div
      role="status"
      className={clsx(
        props.className,
        'flex flex-wrap items-center gap-x-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200'
      )}
    >
      <ExclamationTriangleIcon
        className="size-4 shrink-0 text-amber-500 dark:text-amber-400"
        aria-hidden
      />
      {parts.map((part) => (
        <React.Fragment key={part}>
          <span>{part}</span>
          <span aria-hidden>·</span>
        </React.Fragment>
      ))}
      {typeof props.openLatest === 'string' ? (
        <Link to={props.openLatest} className={actionClass}>
          {actionLabel}
        </Link>
      ) : (
        <button type="button" onClick={props.openLatest} className={actionClass}>
          {actionLabel}
        </button>
      )}
    </div>
  );
};
